import React, { useState } from "react";
import { Box, Typography, makeStyles, Table, TableHead, TableBody, TableRow, TableCell, CircularProgress } from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import SelectField from "components/Select/SelectField";
import AppInput from "components/Input/AppInput";
import { createAppraisal } from "store/slices/appraisal";
import AppConfig from "config/AppConfig";

const useStyles = makeStyles(() => ({
  cardHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#FAFAFA",
    borderRadius: "6px 6px 0 0"
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
  },
  cardContent: {
    padding: "0 20px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
    marginBottom: 20,
  },
  tableHead: {
    background: "#FAFAFA",
  },
  tableCell: {
    padding: "12px 10px",
    textAlign: "left",
    fontWeight: 600,
    color: "#6B7280",
    borderBottom: "1px solid #E5E7EB",
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  tableBodyCell: {
    padding: "12px 10px",
    borderBottom: "1px solid #F3F4F6",
    color: "#111827",
    verticalAlign: "middle",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginBottom: 20,
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: "#374151",
    marginBottom: 8,
    display: "block",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    padding: "16px 20px",
    borderTop: "1px solid #E5E7EB",
  },
}));

const AppraisalModal = ({ open, onClose, staffDetail, onSuccess }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ratingOptions = [
    { value: AppConfig.APPRAISAL_RATING.MEETS, label: t("appraisalRating.MEETS_EXPECTATION") },
    { value: AppConfig.APPRAISAL_RATING.EXCEEDS, label: t("appraisalRating.EXCEEDS_EXPECTATION") },
    { value: AppConfig.APPRAISAL_RATING.BELOW, label: t("appraisalRating.BELOW_EXPECTATIONS") },
  ];

  const overallRatingOptions = [
    { value: AppConfig.APPRAISAL_RATING.MEETS, label: t("appraisalRating.MEETS_EXPECTATION") },
    { value: AppConfig.APPRAISAL_RATING.EXCEEDS, label: t("appraisalRating.EXCEEDS_EXPECTATION") },
    { value: AppConfig.APPRAISAL_RATING.BELOW, label: t("appraisalRating.BELOW_EXPECTATIONS") },
    { value: AppConfig.APPRAISAL_RATING.NEEDS_IMPROVEMENT, label: t("appraisalRating.NEED_IMPROVEMENT") },
  ];

  const criteriaList = [
    { key: "shiftAttendance", label: t("appraisalCriteria.shiftAttendance") },
    { key: "carePlanImplementation", label: t("appraisalCriteria.carePlanImplementation") },
    { key: "incidentReporting", label: t("appraisalCriteria.incidentReporting") },
    { key: "ndisCompliance", label: t("appraisalCriteria.ndisCompliance") },
  ];

  const initialValues = {
    workerId: staffDetail?.id || "",
    shiftAttendance: "",
    carePlanImplementation: "",
    incidentReporting: "",
    ndisCompliance: "",
    overallRating: "",
    strengths: "",
    areasDevelopment: "",
    agreedActions: "",
  };

  const validationSchema = Yup.object().shape({
    workerId: Yup.number().required(t("appraisalLabel.workerIdRequired")),
    shiftAttendance: Yup.string().required(t("validation.required")),
    carePlanImplementation: Yup.string().required(t("validation.required")),
    incidentReporting: Yup.string().required(t("validation.required")),
    ndisCompliance: Yup.string().required(t("validation.required")),
    overallRating: Yup.string().required(t("validation.required")),
    strengths: Yup.string().required(t("validation.required")),
    areasDevelopment: Yup.string().required(t("validation.required")),
    agreedActions: Yup.string().required(t("validation.required")),
  });

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...values,
        status: AppConfig.APPRAISAL_STATUS.CURRENT, // Mặc định status khi tạo mới (nếu BE yêu cầu)
      };

      const result = await dispatch(createAppraisal(payload));
      if (createAppraisal.fulfilled.match(result)) {
        onSuccess && onSuccess();
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ submitForm }) => (
          <Form style={{ position: "relative" }}>
            {isSubmitting && (
              <Box position="absolute" top={0} left={0} right={0} bottom={0} bgcolor="rgba(255,255,255,0.6)" zIndex={10} display="flex" justifyContent="center" alignItems="center" borderRadius={8}>
                <CircularProgress size={32} style={{ color: AppColor.mainColor }} />
              </Box>
            )}
            <Box className={classes.cardContent}>
              <Box style={{ overflowX: "auto" }}>
                <Table className={classes.table}>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableCell} style={{ width: "60%" }}>{t("appraisalLabel.criteria")}</TableCell>
                      <TableCell className={classes.tableCell} style={{ width: "40%" }}>{t("appraisalLabel.rating")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {criteriaList.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell className={classes.tableBodyCell}>
                          {row.label} <span style={{ color: AppColor.red }}>*</span>
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          <Field
                            component={SelectField}
                            name={row.key}
                            options={ratingOptions}
                            placeholder={t("appraisalLabel.selectRating")}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell className={classes.tableBodyCell}>
                        <strong>{t("appraisalLabel.overallRating")}</strong> <span style={{ color: AppColor.red }}>*</span>
                      </TableCell>
                      <TableCell className={classes.tableBodyCell}>
                        <Field
                          component={SelectField}
                          name="overallRating"
                          options={overallRatingOptions}
                          placeholder={t("appraisalLabel.selectRating")}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              <Box>
                <label className={classes.label}>
                  {t("appraisalLabel.strengthsNoted")} <span style={{ color: AppColor.red }}>*</span>
                </label>
                <Field
                  component={AppInput}
                  name="strengths"
                  multiline
                  rows={3}
                  type="textarea"
                  placeholder={t("appraisalLabel.enterStrengths")}
                />
              </Box>
              <Box>
                <label className={classes.label}>
                  {t("appraisalLabel.areasForDevelopment")} <span style={{ color: AppColor.red }}>*</span>
                </label>
                <Field
                  component={AppInput}
                  name="areasDevelopment"
                  multiline
                  rows={3}
                  type="textarea"
                  placeholder={t("appraisalLabel.enterAreasForDevelopment")}
                />
              </Box>

              <Box>
                <label className={classes.label}>
                  {t("appraisalLabel.agreedActions")} <span style={{ color: AppColor.red }}>*</span>
                </label>
                <Field
                  component={AppInput}
                  name="agreedActions"
                  multiline
                  rows={3}
                  type="textarea"
                  placeholder={t("appraisalLabel.enterAgreedActions")}
                />
              </Box>
            </Box>
            <Box className={classes.modalActions}>
              <AppButton
                label={t("button.cancel")}
                mode="outlined"
                onClick={onClose}
                disabled={isSubmitting}
              />
              <AppButton
                label={t("button.save_changes")}
                buttonColor={AppColor.mainColor}
                onClick={submitForm}
                disabled={isSubmitting}
                isLoading={isSubmitting}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default AppraisalModal;

